import ComponentManager from '@/Management/ComponentManager';
import { IColumnDTO, IDetailViewDTO, IDVGItemDTO, IInlineEditingContextDTO, IInlineEditingGridColumnDTO, IListViewDTO } from "coreframetypings/dto";
import Utilities from '@/Utility/Utilities';
import ResourceManager from '@/Management/ResourceManager';
import { CustomizedInlineColumDefinition, GridColumn, IExplicitCustomizedInlineColumDefinition, IImplicitCustomizedInlineColumDefinition } from '#/DefaultViewExtensionInterfaces';

export default class InlineEditingGridColumnHelper {

    public static createColumnDefinitionFromDetailView(associationListView: IListViewDTO, detailView: IDetailViewDTO, customizing: CustomizedInlineColumDefinition): IInlineEditingGridColumnDTO[] {
        return InlineEditingGridColumnHelper.createColumnDefinition(associationListView, detailView, customizing, 'DV');
    }

    public static createColumnDefinitionFromAssociationListView(associationListView: IListViewDTO, detailView: IDetailViewDTO, customizing: CustomizedInlineColumDefinition): IInlineEditingGridColumnDTO[] {
        return InlineEditingGridColumnHelper.createColumnDefinition(associationListView, detailView, customizing, 'ALV');
    }

    public static getNeededPropertiesForEditing(selectedColumns: GridColumn[], inlineEditingContext: IInlineEditingContextDTO): string[] {
        let neededProperties = [];
        selectedColumns.forEach((column) => {
            if (column.hidden)
                return;

            const field = column.field;
            if (Utilities.isNullOrUndefinedOrBlank(field))
                return;

            InlineEditingGridColumnHelper.collectRelevantNeededProperties(column.field, inlineEditingContext.detailView, neededProperties);
        });

        if (neededProperties.indexOf('PDO_ID') == -1)
            neededProperties.splice(0, 0, 'PDO_ID');

        return neededProperties;
    }

    private static collectRelevantNeededProperties(property: string, detailView: IDetailViewDTO, properties: string[]): void {
        const mainProperty = InlineEditingGridColumnHelper.extractMemberNameFromStruct(property);

        let relevantProperties = detailView.neededProperties.filter(p => p === mainProperty || p.startsWith(mainProperty + '.'));
        // e.g. toManyAssociations
        if (relevantProperties.length == 0)
            properties.push(property);
        else {
            relevantProperties.forEach(p => properties.push(p));
        }
    }

    private static createColumnDefinition(associationListView: IListViewDTO, detailView: IDetailViewDTO, customizing: CustomizedInlineColumDefinition, base: 'ALV' | 'DV'): IInlineEditingGridColumnDTO[] {
        let result: IInlineEditingGridColumnDTO[] = [];

        if (InlineEditingGridColumnHelper.isImplicitCustomizing(customizing)) {
            let dvgItems: IDVGItemDTO[] = InlineEditingGridColumnHelper.getDetailViewItems(detailView, associationListView, base);
            for (let dvgItem of dvgItems) {
                let memberName = InlineEditingGridColumnHelper.extractMemberNameFromDVGItem(dvgItem, detailView);
                let memberNameShorten = this.cleanUpMemberName(memberName);
                if (InlineEditingGridColumnHelper.isMemberExcluded(memberNameShorten, customizing))
                    continue;

                let column = InlineEditingGridColumnHelper.getOrCreateColumnFromMemberName(memberName, detailView, associationListView.columns);

                if (InlineEditingGridColumnHelper.isMemberHidden(memberName, customizing))
                    column.isVisible = false;

                result.push(column);
            }

            for (let additionalMember of customizing.additionalMembers || []) {
                const column = InlineEditingGridColumnHelper.getOrCreateColumnFromMemberName(additionalMember, detailView, associationListView.columns, true);
                if (Utilities.isNullOrUndefined(column))
                    continue;

                result.push(column);
            }
        }
        else {
            for (let visibleMember of customizing.visibleMembers || []) {
                const column = InlineEditingGridColumnHelper.getOrCreateColumnFromMemberName(visibleMember, detailView, associationListView.columns, true);
                if (Utilities.isNullOrUndefined(column))
                    continue;

                result.push(column);
            }

            for (let hiddenMember of customizing.hiddenMembers || []) {
                const column = InlineEditingGridColumnHelper.getOrCreateColumnFromMemberName(hiddenMember, detailView, associationListView.columns, false);
                if (Utilities.isNullOrUndefined(column))
                    continue;

                result.push(column);
            }
        }

        return result;
    }

    private static cleanUpMemberName(memberName: string): string {
        if (memberName.indexOf('.') > 0) {
            return memberName.substring(0, memberName.indexOf('.'));
        }

        return memberName;
    }

    private static getOrCreateColumnFromMemberName(name: string, detailView: IDetailViewDTO, columns: IColumnDTO[], isVisible?: boolean): IInlineEditingGridColumnDTO {
        let dvgItem = InlineEditingGridColumnHelper.getDetailViewItem(name, detailView);
        if (!Utilities.isNullOrUndefined(dvgItem)) {
            const column = InlineEditingGridColumnHelper.getColumn(name, columns) || InlineEditingGridColumnHelper.createFakeColumnFromDetailViewItem(name, dvgItem);

            if (column != null)
                column.name = column.name.replace(".ListViewValue", "");
            
            if (!Utilities.isNullOrUndefined(isVisible))
                column.isVisible = isVisible;

            let inlineColumn: IInlineEditingGridColumnDTO = $.extend({}, column, {
                detailViewItem: dvgItem,
                alwaysVisible: dvgItem.required,
                listViewPDOID: dvgItem.listView?.listViewID
            });

            return inlineColumn;
        }       

        return null;
    }


    private static createFakeColumnFromDetailViewItem(name: string, dvgItem: IDVGItemDTO): IInlineEditingGridColumnDTO {
        return {
            listViewColumnFactory: '',
            sortable: false,
            unitNames: {},
            possiblePDEnumerationValues: {},
            uiGenerator: '',
            listViewPDOID: '',
            dotNetTypeName: dvgItem.dotNetTypeName,
            name: name,
            isFilterable: false,
            mlsErgName: {
                [ResourceManager.getInstance().getLanguage().iso6391]: ResourceManager.getInstance().get(dvgItem.ergName)
            },
            isVisible: true,
            alwaysVisible: dvgItem.required,
            detailViewItem: dvgItem
        };
    }

    private static getColumn(propertyName: string, columns: IColumnDTO[]): IColumnDTO {
        return _.find(columns, column => propertyName === InlineEditingGridColumnHelper.extractMemberNameFromColumn(column));
    }

    private static findDetailViewItem(propertyName: string, detailView: IDetailViewDTO, items: IDVGItemDTO[]): IDVGItemDTO {
        let found = _.find(items, item => propertyName === InlineEditingGridColumnHelper.extractMemberNameFromDVGItem(item, detailView));

        // Wird von der DetailView ausgegangen, so ist propertyName der Name des structs
        // Wir geben hier erst einmal nur ein einziges Kind (z.B. bei MLString) zurück
        // Für Structs mit mehr Eigenschaften müsste diese Methode ein Array (found.children) zurückgeben.
        if (!Utilities.isNullOrUndefined(found)) {
            if (found.children.length == 1) {
              	const child = found.children[0];
              	child.ergName = found.ergName;
                child.length = found.length;
                child.tooltip = found.tooltip;
                child.parent = InlineEditingGridColumnHelper.createParentDVGItem(found);
                return child;
            }
            return found;
        }

        items.some(item => {
            found = InlineEditingGridColumnHelper.findDetailViewItem(propertyName, detailView, item.children);

            // Bekannte Einschränkung: Structs, bei denen mehrere Eigenschaften im Grid angezeigt werden sollen, bekommen aktuell den gleichen Namen
            // Ursache: Eigenschaften wie "Value" sollen den ErgNamen des Structs erhalten
            if (found) {
                found.ergName = item.ergName;
                found.length = item.length;
                found.tooltip = item.tooltip;
                found.parent = InlineEditingGridColumnHelper.createParentDVGItem(item);
            }

            return !Utilities.isNullOrUndefined(found);
        });

        return found;
    }

    private static createParentDVGItem(item: IDVGItemDTO): Partial<IDVGItemDTO> {
        return {
            uiGenerator: item.uiGenerator,
            dotNetTypeName: item.dotNetTypeName
        }
    }

    private static getDetailViewItem(propertyName: string, detailView: IDetailViewDTO): IDVGItemDTO {
        return InlineEditingGridColumnHelper.findDetailViewItem(propertyName, detailView, detailView.detailViewGroups[0].items)
    }

    private static getDetailViewItems(detailView: IDetailViewDTO, associationListView: IListViewDTO, base: 'DV' | 'ALV'): IDVGItemDTO[] {
        if (base == 'DV') {
            return detailView.detailViewGroups[0].items;
        }
        else if (base == 'ALV') {
            return associationListView.columns.map((column) => {
                let memberName = InlineEditingGridColumnHelper.extractMemberNameFromColumn(column);
                return InlineEditingGridColumnHelper.getDetailViewItem(memberName, detailView);
            }).filter(x => x != null);
        }

        return [];
    }

    private static extractMemberNameFromColumn(column: IColumnDTO): string {
        if (_.keys(column.possiblePDEnumerationValues).length == 0) {
            return column.name
                .replace('.ObjectIdentValue', '')
                .replace('.ListViewValue', '');
        }
        else {
            // mapping for PDEmbeddeds 
            return column.name.replace('.MLSValue', '.Value');
        }
    }

    private static extractMemberNameFromStruct(property: string): string {
        return property.replace('.ObjectIdentValue', '').replace('.MLSValue', '').replace('.EURValue', '').replace('.Value', '');
    }

    private static extractMemberNameFromDVGItem(detailViewItem: IDVGItemDTO, detailView: IDetailViewDTO): string {
        const typeName = detailView.typeName
        return detailViewItem.name.replace(typeName + '_', '');
    }

    private static isMemberExcluded(memberName: string, customizing: IImplicitCustomizedInlineColumDefinition) {
        return !Utilities.isNullOrUndefined(customizing.excludedMembers) && customizing.excludedMembers.length > 0 && customizing.excludedMembers.indexOf(memberName) > -1;
    }

    private static isMemberHidden(memberName: string, customizing: IImplicitCustomizedInlineColumDefinition) {
        return !Utilities.isNullOrUndefined(customizing.hiddenMembers) && customizing.hiddenMembers.length > 0 && customizing.hiddenMembers.indexOf(memberName) > -1;
    }

    private static isImplicitCustomizing(customizing: CustomizedInlineColumDefinition): customizing is IImplicitCustomizedInlineColumDefinition  {
        let implicit = customizing as IImplicitCustomizedInlineColumDefinition
        let explicit = customizing as IExplicitCustomizedInlineColumDefinition
        // no customizing -> implicit
        if (_.keys(customizing).length == 0) {
            return true;
        }
        else if ((implicit.excludedMembers && implicit.excludedMembers.length > 0)
            || (implicit.additionalMembers && implicit.additionalMembers.length > 0)) {
            return true;
        } else if ((explicit.visibleMembers && explicit.visibleMembers.length > 0)) {
            return false;
        }

        return true;
    }

    /**
     * Nimmt eine Spalte und fügt sie hinter einer anderen ein
     * @param columnDefinition
     * @param column
     * @param insertAfter
     */
    public static pickColumnAndInsertBehind(columnDefinition: IInlineEditingGridColumnDTO[], column: string, insertAfter: string): IInlineEditingGridColumnDTO[] {
        const changeColumn = columnDefinition.filter(x => x.detailViewItem.name == column)[0];
        const changeIndex = columnDefinition.indexOf(changeColumn);
        const insertAfterColumn = columnDefinition.filter(x => x.detailViewItem.name == insertAfter)[0];
        const insertAfterIndex = columnDefinition.indexOf(insertAfterColumn);

        if (changeIndex == -1 || insertAfterIndex == -1)
            return columnDefinition;

        columnDefinition.splice(changeIndex, 1);
        columnDefinition.splice(insertAfterIndex + 1, 0, changeColumn);

        return columnDefinition;
    }
}

ComponentManager.setUtility('InlineEditingGridColumnHelper', InlineEditingGridColumnHelper, 'RADPresentation.HTMLClient.DefaultViewExtensions');